import { create } from 'zustand'

type Theme = 'light' | 'dark'

const THEME_KEY = 'optimus.theme'

function systemPrefersDark(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function readInitial(): { theme: Theme; explicit: boolean } {
    try {
        const stored = localStorage.getItem(THEME_KEY)
        if (stored === 'light' || stored === 'dark')
            return { theme: stored, explicit: true }
    } catch {
        // localStorage nicht verfügbar — System-Preference nutzen
    }
    return { theme: systemPrefersDark() ? 'dark' : 'light', explicit: false }
}

interface ThemeState {
    theme: Theme
    /** false = folgt noch der System-Einstellung (keine explizite Wahl). */
    explicit: boolean
    toggle: () => void
}

function applyTheme(theme: Theme) {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    document.documentElement.style.colorScheme = theme
}

// Initial-Wert beim Modul-Import anwenden — läuft vor dem ersten React-Render
// und verhindert so ein Aufblitzen des falschen Themes.
const initial = readInitial()
applyTheme(initial.theme)

export const useThemeStore = create<ThemeState>()(set => ({
    theme: initial.theme,
    explicit: initial.explicit,
    toggle: () => {
        const next: Theme =
            useThemeStore.getState().theme === 'dark' ? 'light' : 'dark'
        try {
            localStorage.setItem(THEME_KEY, next)
        } catch {
            // Persistenz ist optional
        }
        applyTheme(next)
        set({ theme: next, explicit: true })
    },
}))

// Systemwechsel nur übernehmen, solange der Nutzer nichts explizit gewählt hat
window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', e => {
        const { explicit } = useThemeStore.getState()
        if (explicit) return
        const theme: Theme = e.matches ? 'dark' : 'light'
        applyTheme(theme)
        useThemeStore.setState({ theme })
    })
